import React, { useState, useEffect, useMemo } from "react";

import { Card } from "@mui/material";
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { loadMembersForAllClientsFromBackend } from "app/services/member.service";
import { loadAllManagersFromBackend } from "app/services/manager.service";
import { checkUserHasAdminRole, readSessionCurrentClient } from "app/services/app.state.service";
import { handleAxiosError } from "app/services/app.error.handler";
import { TeamMemberDataGridBase } from "./TeamMemberDataGridBase";
import MemberActivityDataGridView from "../team-activities/MemberActivityDataGridView";
import { ClientFilterSelect } from "app/components/common/ClientFilterSelect";
import { toClientListOptions } from "utils/clientOptions";
import { toManagerGridRows } from "utils/managerRows";
import { ALL_CLIENTS } from "AppConstants";
import { useDispatch, useSelector } from "react-redux";
import { memberActions } from "store/member-slice";

export const TeamMembersAdminView = () => {
    const dispatch = useDispatch();
    const [selectedMember, setSelectedMember] = useState(null);
    const membersData = useSelector((state) => state.member.rows);
    const [isLoading, setIsLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [managers, setManagers] = useState([]);
    const [teamClientId, setTeamClientId] = useState(ALL_CLIENTS);

    const selectMemberHandler = (memberObj) => {
        setSelectedMember(memberObj);
    }

    // An admin's session already holds every client, so the filter costs no extra call.
    useEffect(() => {
        const list = readSessionCurrentClient();
        setClients(Array.isArray(list) ? list : []);
    }, []);

    useEffect(() =>  {
        if(!checkUserHasAdminRole()) return;

        if(membersData.length === 0)
        {
            setIsLoading(true);
            
            // Set clientId only if role is client 
            loadMembersForAllClientsFromBackend()
            .then(response => {
                dispatch(memberActions.replaceData(response.data));
                setIsLoading(false);
            })
            .catch(error=>{
                dispatch(memberActions.replaceData([]));
                setIsLoading(false);
                handleAxiosError(error);      
            });
        }
    }, [dispatch, membersData.length]);

    // An admin's list covers everyone on the platform, so the managers belong in it as well
    // as the members — a manager is not a member of anything and would otherwise be missing.
    useEffect(() => {
        if(!checkUserHasAdminRole()) return;

        loadAllManagersFromBackend()
        .then(response => {
            setManagers(response.status === 204 || !Array.isArray(response.data) ? [] : response.data);
        })
        .catch(error => {
            setManagers([]);
            handleAxiosError(error);
        });
    }, []);

    const clientOptions = useMemo(() => toClientListOptions(clients), [clients]);

    // Every row is loaded up front, so the client filter narrows the loaded rows
    // rather than re-fetching (HRMS-314).
    const visibleMembers = useMemo(() => {
        const rows = [...toManagerGridRows(managers), ...membersData];
        if (teamClientId === ALL_CLIENTS) return rows;
        return rows.filter(
            (m) => String(m.client?.id ?? m.clientId) === String(teamClientId)
        );
    }, [membersData, managers, teamClientId]);

    // A member of the previous client must not stay open below a grid they no longer appear in.
    const clientChangeHandler = (value) => {
        setTeamClientId(value);
        setSelectedMember(null);
    }
        
    if(!checkUserHasAdminRole()) {
        return <div>You do not have access to this functionality.</div>;
    }
    
    return (
        <div
            className="data-grid-container"
            style={{ width: '100%', minWidth: 0, padding: '0 16px', boxSizing: 'border-box' }}
        >
            <Card>
                <MDBox
                    mx={2}
                    mt={-3}
                    py={1}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                >
                    <MDTypography variant="h6" color="white">
                        Client Team Members
                    </MDTypography>
                </MDBox>

                <MDBox px={2} pt={3}>
                    <ClientFilterSelect
                        value={teamClientId}
                        onChange={clientChangeHandler}
                        options={clientOptions}
                        id="team-members-client"
                    />
                </MDBox>

                <TeamMemberDataGridBase membersData={visibleMembers} isLoading={isLoading} clickEventHandler={selectMemberHandler}/>
            </Card>
            {selectedMember && (
                <div style={{ marginTop: 24 }}>
                    <MemberActivityDataGridView selectedMember={selectedMember}/>
                </div>
            )}
        </div>

    );
};
